import axios from 'axios';
import { storeAction } from "../storeActions.constant";
import { storeGetters } from "../storeGetters.constant";
import { storeMutation } from "../storeMutations.constant";
import { getBasicHeaders } from "../../common/utils/utils";
import { eventDirectorySearchObject, eventDirectoryTypeAheadObject } from "../store.constant";
import { wrapAction } from "../../common/utils/actionLifecycle";
import { getAxiosCancelTokenSource } from "../../app.utils";
import {
    ypoEventConstant,
    defaultYpoEventSort,
    createYpoEventState,
    sortAppliedFiltersAlphabetically,
    getEventFilterAccordions,
    createApplicableLocationFilters,
    getSelectedFilterKeys,
    determineParentChildFilterToApply,
    filterPillsForDateRange,
    sortHostFilters,
} from "@/components/YpoEvents/ypoEvents.constant";

const ypoEventState = () => {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        ypoEvents: null,
        ypoEventsCount: 0,
        ypoEventsRegionsList: null,
        ypoFilterFacets: [],
        ypoEventsSort: { ...defaultYpoEventSort },
        searchResultOffset: 0,
        ypoEventsSelectedFilters: createYpoEventState(),
        ypoEventsSelectedFiltersKeys: [],
        ypoEventSearchText: null,
        ypoEventsUnfilteredFacetState: null,
        mostRecentlySelectedFacet: null,
        ypoEventFrozenFacet: null,
        disableYpoEventsInfiniteScroll: true,
        canFetchMoreResult: true,
        mobileFiltersActive: false,
        userRegisteredAndWaitlistedEventCount: 0,
    };
};

const getEventsRequestBody = (state, getters, skipCount) => {
    const sortObject = getters[storeGetters.ypoEventsSort];
    const sortString = `${sortObject.sortField} ${sortObject.direction}`;
    const selectedFiltersState = { ...state.ypoEventsSelectedFilters };
    selectedFiltersState[ypoEventConstant.constant.keys.locationFilter] =
        createApplicableLocationFilters(selectedFiltersState[ypoEventConstant.constant.keys.locationFilter], true);
    selectedFiltersState[ypoEventConstant.constant.keys.eventFocusFilter] =
        determineParentChildFilterToApply(selectedFiltersState[ypoEventConstant.constant.keys.eventFocusFilter]);
    selectedFiltersState[ypoEventConstant.constant.keys.lifelongLearningFilter] =
        determineParentChildFilterToApply(selectedFiltersState[ypoEventConstant.constant.keys.lifelongLearningFilter]);
    const requestBody = eventDirectorySearchObject(state.ypoEventSearchText, sortString, selectedFiltersState, skipCount, state.userRegisteredAndWaitlistedEventCount);
        requestBody.isAndRelationshipApplied = true;
    return requestBody;
};

const ypoEventsMutations = {
    [storeMutation.setResultForYpoEvents]: (state, resultData) => {
        const { data, setInitialResult } = resultData;
        if (data) {
            let facetFiltersFromData = data.searchRibbonGroup && data.searchRibbonGroup.length
                ? getEventFilterAccordions(data.searchRibbonGroup, state.ypoEventsSelectedFilters)
                : null;

            if (facetFiltersFromData && state.ypoEventFrozenFacet && state.ypoEventFrozenFacet.key
                && state.ypoEventFrozenFacet.categories && state.ypoEventFrozenFacet.categories.length) {
                facetFiltersFromData.forEach((facet) => {
                    if (facet && facet.key && facet.key.toLowerCase() === state.ypoEventFrozenFacet.key.toLowerCase()) {
                       facet.categories = [...state.ypoEventFrozenFacet.categories];
                    }
                });
                facetFiltersFromData = getEventFilterAccordions(facetFiltersFromData, state.ypoEventsSelectedFilters);
            }
            if (setInitialResult) {
                state.ypoEvents = data.searchResults || [];
            } else if (data.searchResults && data.searchResults.length && state.ypoEvents && state.ypoEvents.length) {
                state.ypoEvents = [...state.ypoEvents, ...data.searchResults];
            }
            state.ypoEventsCount = data.totalResultCount || 0;
            state.ypoFilterFacets = facetFiltersFromData || [];
            state.userRegisteredAndWaitlistedEventCount = data.userRegisteredAndWaitlistedEventCount || 0;
            state.canFetchMoreResult = state.ypoEvents && state.ypoEventsCount
                && state.ypoEvents.length < state.ypoEventsCount;
            if (!state.ypoEventsCount) state.mobileFiltersActive = false;
        }
        state.searchResultOffset = setInitialResult
            ? ypoEventConstant.constant.searchResultOffsetStep
            : state.searchResultOffset + ypoEventConstant.constant.searchResultOffsetStep;
    },
    [storeMutation.setUnfilteredYpoEventsState]: (state, facets) => {
        state.ypoEventsUnfilteredFacetState = getEventFilterAccordions(facets);
    },
    [storeMutation.setMobileFiltersActive]: (state) => {
        state.mobileFiltersActive = !state.mobileFiltersActive;
    },
    [storeMutation.setMostRecentlySelectedFacet]: (state, facetKey) => {
        state.mostRecentlySelectedFacet = facetKey || null;
        state.ypoEventFrozenFacet = null;
        if (state.mostRecentlySelectedFacet && state.ypoFilterFacets && state.ypoFilterFacets.length) {
            const newFrozenFacet = state.ypoFilterFacets.find((facet) => {
                return facet.key && facet.key.toLowerCase() === state.mostRecentlySelectedFacet.toLowerCase();
            });
            state.ypoEventFrozenFacet = newFrozenFacet ? { ...newFrozenFacet } : null;
        }
    },
    [storeMutation.resetYpoEvents]: (state) => {
        state.ypoEvents = null;
        state.ypoEventsCount = 0;
        state.searchResultOffset = 0;
    },
    [storeMutation.setYpoEventsSort]: (state, value) => {
        state.ypoEventsSort = value || defaultYpoEventSort;
    },
    [storeMutation.setYpoEventsInfiniteScrollStatus]: (state, value) => {
        state.disableYpoEventsInfiniteScroll = value;
    },
    [storeMutation.setYpoEventStateFromRouter]: (state, dataFromRoute) => {
        const { eventSearchText, filters, sortOrder } = dataFromRoute;
        state.ypoEventsSelectedFilters = createYpoEventState();
        if (filters) {
            state.ypoEventsSelectedFilters = { ...state.ypoEventsSelectedFilters, ...filters };
        }
        state.ypoEventsSelectedFiltersKeys = getSelectedFilterKeys(filters);
        state.ypoEventSearchText = eventSearchText || '';
        state.ypoEventsSort = sortOrder || defaultYpoEventSort;

        if (state.ypoEventsUnfilteredFacetState && state.ypoEventsSelectedFiltersKeys.length === 1 && state.ypoEventsSelectedFiltersKeys[0]) {
            const newFrozenFacet = state.ypoEventsUnfilteredFacetState.find((facet) => {
                return facet.key && facet.key.toLowerCase() === state.ypoEventsSelectedFiltersKeys[0].toLowerCase();
            });
            state.ypoEventFrozenFacet = newFrozenFacet ? { ...newFrozenFacet } : null;
        }
    },
    [storeMutation.setYpoEventsSearchText]: (state, data) => {
        state.ypoEventSearchText = data || '';
    },
    [storeMutation.setYpoEventsRegionsList]: (state, data) => {
        state.ypoEventsRegionsList = data || '';
    },
};

const ypoEventsActions = {
    [storeAction.getUnfilteredYpoEvents]: wrapAction(
        storeAction.getInitialYpoEvents,
        ({
             state,
             commit,
             getters,
             dispatch,
         }) => {
            commit(storeMutation.resetYpoEvents);
            const sortObject = getters[storeGetters.ypoEventsSort];
            const sortString = `${sortObject.sortField} ${sortObject.direction}`;
            const selectedFiltersState = null;
            const requestBody = eventDirectorySearchObject(state.ypoEventSearchText, sortString, selectedFiltersState, 0, state.userRegisteredAndWaitlistedEventCount);
            requestBody.isAndRelationshipApplied = true;
            return dispatch(storeAction.getYpoEvents, requestBody)
                .then((response) => {
                    if (response && response.data && response.data.searchRibbonGroup) {
                        commit(storeMutation.setUnfilteredYpoEventsState, response.data.searchRibbonGroup);
                    }
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getInitialYpoEvents]: wrapAction(
        storeAction.getInitialYpoEvents,
        ({
             state,
             commit,
             getters,
             dispatch,
         }) => {
            commit(storeMutation.resetYpoEvents);
            const requestBody = getEventsRequestBody(state, getters, 0);
            return dispatch(storeAction.getYpoEvents, requestBody)
                .then((response) => {
                    const resultData = {
                        data: response.data,
                        setInitialResult: true,
                    };
                    commit(storeMutation.setResultForYpoEvents, resultData);
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                })
                .finally(() => {
                    commit(storeMutation.setYpoEventsInfiniteScrollStatus, false);
                });
        },
    ),
    [storeAction.getMoreYpoEvents]: wrapAction(
        storeAction.getMoreYpoEvents,
        ({
             state,
             commit,
             getters,
             dispatch,
         }) => {
            commit(storeMutation.setYpoEventsInfiniteScrollStatus, true);
            const requestBody = getEventsRequestBody(state, getters, state.searchResultOffset);
            return dispatch(storeAction.getYpoEvents, requestBody)
                .then((response) => {
                    const resultData = {
                        data: response.data,
                        setInitialResult: false,
                    };
                    commit(storeMutation.setResultForYpoEvents, resultData);
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                })
                .finally(() => {
                    commit(storeMutation.setYpoEventsInfiniteScrollStatus, false);
                });
        },
    ),
    [storeAction.getYpoEvents]: wrapAction(
        storeAction.getYpoEvents,
        ({ state }, requestBody) => {
            return axios({
                // url: '/mocks/eventDirectory.json',
                 url: `${state.baseURL}events/api/eventsearch`,
                headers: getBasicHeaders(),
                data: requestBody,
                // method: 'GET',
                 method: 'POST',
            });
        },
    ),    
    [storeAction.getYpoEventsTypeAheadSearch]: wrapAction(
        storeAction.getYpoEventsTypeAheadSearch,
        ({ state, commit }, typeAheadData) => {
            //handling race condition
            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.getYpoEventsTypeAheadSearch,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            const {
                searchText,
                selectedTypeAhead,
                selectedFilters,
                additionalFilterToQuery,
            } = typeAheadData;
            const requestBody = eventDirectoryTypeAheadObject(searchText, state.eventSearchText, selectedTypeAhead, selectedFilters, additionalFilterToQuery);
            //const requestBody = {                search: searchText,            };
            return axios({
                url: `${state.baseURL}communities/api/event-directory-autocomplete`,
                data: requestBody,
                cancelToken: requestSource.token,
                headers: getBasicHeaders(),
                method: 'POST',
            })
                .then((response) => {
                    commit(storeMutation.removeCancelableRequest, storeAction.getYpoEventsTypeAheadSearch, { root: true });
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getYpoEventsRegionsList]: ({ state, commit }) => {                
            return axios({
                url: `${state.baseURL}communities/api/communities/get-regions`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
            
                .then((response) => {
                    commit(storeMutation.setYpoEventsRegionsList, response.data);
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
};

const ypoEventsGetters = {
    [storeGetters.ypoEventsList]: state => state.ypoEvents,
    [storeGetters.ypoEventsRegionsList]: state => state.ypoEventsRegionsList,
    [storeGetters.ypoEventsResultCount]: state => state.ypoEventsCount,
    [storeGetters.ypoEventsFacets]: state => state.ypoFilterFacets,
    [storeGetters.ypoEventsSort]: state => state.ypoEventsSort,
    [storeGetters.ypoEventsAppliedFilterPills]: (state) => {
        let pills = [];
        const { ypoEventsSelectedFilters } = state;
        if (ypoEventsSelectedFilters && Object.keys(ypoEventsSelectedFilters).length) {
            const appliedFilters = { ...ypoEventsSelectedFilters };
            Object.keys(appliedFilters).forEach((key) => {
                if (appliedFilters.hasOwnProperty(key) && appliedFilters[key].length > 0) {
                    if (key === ypoEventConstant.constant.keys.locationFilter) {
                        appliedFilters[key] = createApplicableLocationFilters(appliedFilters[key]);
                    }
                    if (key === ypoEventConstant.constant.keys.eventFocusFilter || key === ypoEventConstant.constant.keys.lifelongLearningFilter) {
                        appliedFilters[key] = determineParentChildFilterToApply(appliedFilters[key]);
                    }
                    if (appliedFilters[key].length) {
                       appliedFilters[key].map((item) => {
                           item.parentKey = key;
                           return item;
                       });
                       const sortedAppliedFilters = key === ypoEventConstant.constant.keys.hostFilter
                           ? sortHostFilters(appliedFilters[key])
                           : sortAppliedFiltersAlphabetically(appliedFilters[key]);

                        pills.push(...sortedAppliedFilters);
                    }
                    if (pills.length) {
                        pills.forEach((pill) => {
                            const pillType = ypoEventConstant.filterPillWithType
                                .find(item => item.key === pill.filterKey);
                            pill.label = pillType && pillType.label && !pill.label.includes(pillType.label)
                                ? `${pillType.label} ${pill.label}`
                                : pill.label;
                        });
                    }
                }
            });
            pills = pills.length ? filterPillsForDateRange(pills) : [];
        }
        return pills;
    },
    [storeGetters.ypoEventsSelectedFilters]: state => state.ypoEventsSelectedFilters,
    [storeGetters.disableYpoEventsInfiniteScroll]: state => state.disableYpoEventsInfiniteScroll,
    [storeGetters.ypoEventResultOffset]: state => state.searchResultOffset,
    [storeGetters.canFetchMoreResult]: state => state.canFetchMoreResult,
    [storeGetters.mobileFiltersActive]: state => state.mobileFiltersActive,
};

export default {
    namespaced: true,
    state: {
        initialState: ypoEventState(),
        ...ypoEventState(),
    },
    mutations: { ...ypoEventsMutations },
    actions: { ...ypoEventsActions },
    getters: { ...ypoEventsGetters },
};
